import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatDateTimeWAT } from "@/lib/format";
import { Send } from "lucide-react";

export const Messenger = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null); // {to_user_id, name, thread_id}
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");

  const load = useCallback(async () => {
    const [u, t] = await Promise.all([api.get("/users"), api.get("/messages/threads")]);
    setUsers(u.data);
    setThreads(t.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const otherUsers = useMemo(() => users.filter(u => u.id !== user?.id), [users, user?.id]);

  const openThread = async (other) => {
    const thread_id = [user.id, other.id].sort().join(":");
    setSelected({ to_user_id: other.id, name: other.name, thread_id });
    const r = await api.get(`/messages?thread_id=${thread_id}`);
    setMessages(r.data);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() || !selected) return;
    await api.post("/messages", { thread_id: selected.thread_id, to_user_id: selected.to_user_id, body });
    setBody("");
    const r = await api.get(`/messages?thread_id=${selected.thread_id}`);
    setMessages(r.data);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" style={{ height: "calc(100vh - 180px)" }}>
      <div className="s-card" style={{ padding: 0, overflowY: "auto" }}>
        <div className="p-4 border-b" style={{ borderColor: "var(--border-dark)" }}>
          <div className="h-section">Conversations</div>
        </div>
        <div>
          {otherUsers.map(u => (
            <button key={u.id} onClick={() => openThread(u)}
                    className="w-full text-left p-4 flex items-center gap-3"
                    style={{ borderBottom: "1px solid var(--border-dark)",
                             background: selected?.to_user_id === u.id ? "rgba(46,202,200,0.08)" : "transparent" }}
                    data-testid={`thread-${u.id}`}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(46,202,200,0.15)",
                            display: "flex", alignItems: "center", justifyContent: "center", color: "#2ECAC8" }}>
                {u.name?.[0] || "?"}
              </div>
              <div className="flex-1">
                <div style={{ fontSize: 14, color: "#fff" }}>{u.name}</div>
                <div className="text-muted" style={{ fontSize: 12 }}>{u.role.replace("_", " ")}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="lg:col-span-2 s-card" style={{ padding: 0, display: "flex", flexDirection: "column" }}>
        {selected ? (
          <>
            <div className="p-4 border-b" style={{ borderColor: "var(--border-dark)" }}>
              <div style={{ fontSize: 15, color: "#fff" }}>{selected.name}</div>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
              {messages.length === 0 && <div className="text-muted text-center" style={{ fontSize: 13 }}>No messages yet — send the first one.</div>}
              {messages.map(m => {
                const mine = m.from_user_id === user.id;
                return (
                  <div key={m.id} style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", marginBottom: 10 }}>
                    <div style={{
                      maxWidth: "70%", padding: "10px 14px", borderRadius: 12,
                      background: mine ? "var(--brand-teal)" : "#1F3245",
                      color: "#fff", fontSize: 14,
                    }}>
                      {m.body}
                      <div style={{ fontSize: 10, marginTop: 4, opacity: 0.7 }}>{formatDateTimeWAT(m.created_at)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <form onSubmit={send} className="p-4 border-t flex gap-2" style={{ borderColor: "var(--border-dark)" }}>
              <input value={body} onChange={(e) => setBody(e.target.value)} className="s-input" placeholder="Type a message..." data-testid="msg-input" />
              <button type="submit" className="btn-primary" data-testid="msg-send"><Send size={16} /></button>
            </form>
          </>
        ) : (
          <div className="text-muted p-8 text-center" style={{ fontSize: 13 }}>Select a conversation to begin.</div>
        )}
      </div>
    </div>
  );
};
